import React from 'react';
import { TrendingUp, Users, MapPin, Package } from 'lucide-react';
import KPICard from '../components/KPICard';
import { CategoryPieChart, RegionalBarChart, TrendLineChart } from '../components/Charts';
import DataTable from '../components/DataTable';
import {
  useRevenueByCategory,
  useRegionalPerformance,
  useTopProducts,
  useTrendData,
} from '../hooks/useReports';
import { formatCurrency, formatNumber } from '../utils/formatters';

export default function Analytics() {
  const { data: categoryData, isLoading: categoryLoading } = useRevenueByCategory();
  const { data: regionalData, isLoading: regionalLoading } = useRegionalPerformance();
  const { data: topProducts, isLoading: productsLoading } = useTopProducts(15);
  const { data: orderTrend, isLoading: trendLoading } = useTrendData('orders', 'weekly');

  const totalCategoryRevenue = categoryData?.data?.reduce(
    (sum, item) => sum + parseFloat(item.revenue || 0),
    0
  ) || 0;

  const totalRegionalCustomers = regionalData?.data?.reduce(
    (sum, item) => sum + parseInt(item.customers || 0),
    0
  ) || 0;

  const topCategory = categoryData?.data?.[0]?.category || '-';
  const topRegion = regionalData?.data?.[0]?.region || '-';

  const analyticsKPIs = [
    {
      title: 'Total Categories',
      value: categoryData?.data?.length || 0,
      icon: Package,
      format: 'number',
    },
    {
      title: 'Top Category',
      value: topCategory,
      icon: TrendingUp,
      format: 'text',
    },
    {
      title: 'Active Regions',
      value: regionalData?.data?.length || 0,
      icon: MapPin,
      format: 'number',
    },
    {
      title: 'Total Customers',
      value: totalRegionalCustomers,
      icon: Users,
      format: 'compact',
    },
  ];

  const categoryColumns = [
    { key: 'category', label: 'Category', sortable: true },
    { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
    { key: 'transactions', label: 'Transactions', sortable: true, format: 'number' },
    {
      key: 'share',
      label: 'Market Share',
      sortable: true,
      format: 'percentage',
    },
  ];

  const regionalColumns = [
    { key: 'region', label: 'Region', sortable: true },
    { key: 'customers', label: 'Customers', sortable: true, format: 'number' },
    { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
    { key: 'avg_transaction', label: 'Avg Transaction', sortable: true, format: 'currency' },
  ];

  const productColumns = [
    { key: 'product_name', label: 'Product', sortable: true },
    { key: 'units_sold', label: 'Units Sold', sortable: true, format: 'number' },
    { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
  ];

  const enrichedCategoryData = categoryData?.data?.map((item) => ({
    ...item,
    share: totalCategoryRevenue > 0 ? (parseFloat(item.revenue) / totalCategoryRevenue) * 100 : 0,
  }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {analyticsKPIs.map((kpi, index) => (
          <KPICard
            key={index}
            {...kpi}
            loading={categoryLoading || regionalLoading}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="card-header">Category Distribution</h3>
          <CategoryPieChart data={categoryData?.data} loading={categoryLoading} />
        </div>

        <div className="card">
          <h3 className="card-header">Regional Revenue</h3>
          <RegionalBarChart data={regionalData?.data} loading={regionalLoading} />
        </div>
      </div>

      <div className="card">
        <h3 className="card-header">Order Trends (Weekly)</h3>
        <TrendLineChart data={orderTrend?.data} loading={trendLoading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="card-header">Category Performance</h3>
          <DataTable
            data={enrichedCategoryData}
            columns={categoryColumns}
            loading={categoryLoading}
            emptyMessage="No category data available"
          />
        </div>

        <div className="card">
          <h3 className="card-header">Regional Breakdown</h3>
          <DataTable
            data={regionalData?.data}
            columns={regionalColumns}
            loading={regionalLoading}
            emptyMessage="No regional data available"
          />
        </div>
      </div>

      <div className="card">
        <h3 className="card-header">Product Performance Analysis</h3>
        <DataTable
          data={topProducts?.data}
          columns={productColumns}
          loading={productsLoading}
          emptyMessage="No product data available"
        />
      </div>
    </div>
  );
}
